// State + side effects for the AI incident chat.
// Owns: messages array, extracted fields, confidence, missing list,
// readyToCreate flag, createdIncident, confetti trigger, active suggestion,
// busy state, turn counter, and the sendTurn() side effect that talks to
// /api/incidents/ai-create.
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useStore } from "../../store";
import {
  API,
  EMPTY_EXTRACTED,
  MAX_TURNS,
} from "./aiChatConstants";

export const useChatState = () => {
  const addToast = useStore((s) => s.addToast);

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [extracted, setExtracted] = useState(EMPTY_EXTRACTED);
  const [confidence, setConfidence] = useState("low");
  const [missing, setMissing] = useState(["title", "category", "severity"]);
  const [readyToCreate, setReadyToCreate] = useState(false);
  const [createdIncident, setCreatedIncident] = useState(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const [activeSuggestion, setActiveSuggestion] = useState(null);
  // Iter33: briefing-derived situational chips (max 2). Fetched once on mount
  // with a 2s timeout — silent failure leaves only the 4 static chips visible.
  const [briefing, setBriefing] = useState(null);

  const scrollRef = useRef(null);
  const taRef = useRef(null);

  const userTurns = messages.filter((m) => m.role === "user").length;
  const hitTurnLimit = userTurns >= MAX_TURNS && !createdIncident;

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, busy]);

  useEffect(() => {
    if (taRef.current) {
      taRef.current.style.height = "auto";
      const next = Math.min(taRef.current.scrollHeight, 96);
      taRef.current.style.height = `${next}px`;
    }
  }, [input]);

  // Iter33: briefing fetch with 2s timeout — silent fail.
  useEffect(() => {
    const ac = new AbortController();
    const timer = setTimeout(() => ac.abort(), 2000);
    axios
      .get(`${API}/briefing`, { signal: ac.signal })
      .then((r) => setBriefing(r.data || null))
      .catch(() => { /* best-effort — static chips still render */ })
      .finally(() => clearTimeout(timer));
    return () => {
      clearTimeout(timer);
      ac.abort();
    };
  }, []);

  const sendTurn = async (userText, opts = {}) => {
    if (!userText.trim() && !opts.confirm) return;
    const newUserMsg = userText.trim() ? { role: "user", content: userText.trim() } : null;
    const optimistic = newUserMsg ? [...messages, newUserMsg] : messages;
    if (newUserMsg) setMessages(optimistic);
    setInput("");
    setBusy(true);

    try {
      const history = optimistic
        .filter((m) => m.content)
        .map((m) => ({ role: m.role, content: m.content }));

      const { data } = await axios.post(`${API}/incidents/ai-create`, {
        messages: history,
        confirm: Boolean(opts.confirm),
      });

      const assistantContent = JSON.stringify({
        action: data.action,
        response: data.response,
        extracted: data.extracted,
        missing_required: data.missing_required,
        confidence: data.confidence,
      });

      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content: assistantContent,
          display: data.response,
          action: data.action,
          incident: data.incident || null,
        },
      ]);
      setExtracted(data.extracted || EMPTY_EXTRACTED);
      setConfidence(data.confidence || "low");
      setMissing(data.missing_required || []);
      setReadyToCreate(data.action === "ready_to_create");
      setActiveSuggestion(data.suggestion || null);
      if (data.action === "created" && data.incident) {
        setCreatedIncident(data.incident);
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 3500);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || "Network error";
      addToast({ type: "info", title: "AI assistant error", body: detail });
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content: "",
          display: `Sorry — ${detail}. Try again or switch to the wizard.`,
        },
      ]);
    } finally {
      setBusy(false);
    }
  };

  return {
    // state
    messages, input, busy, extracted, confidence, missing,
    readyToCreate, createdIncident, showConfetti, activeSuggestion,
    userTurns, hitTurnLimit, briefing,
    // refs
    scrollRef, taRef,
    // setters / actions
    setInput, setActiveSuggestion, sendTurn,
  };
};
